import deepEqual from 'deep-equal'
import React, {
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { AddUserLanguage, LanguageErrorCodes } from '../../../i18n'
import { userRoleURL } from '../../NonReact/constants/ApiConstants'
import { EMPTY_STRING, LOADING_STRING } from '../../NonReact/constants/Common'
import { HIDE } from '../../NonReact/constants/class-names'
import {
    DeepMap,
    FieldError,
    FieldValues,
    FormProvider,
    useForm,
    UseFormMethods
} from '../../Vendor/react-hook-form/dist/index.ie11'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { RESTAPIError } from '../Common/API'
import { ALL_VALUE } from '../Common/API/ItemsGetters'
import {
    isCxsSubscriptionApplicabilityPendingForAddUser,
    isCxsSubscriptionScopeAllowedForAddUser
} from '../Common/Utils/CxsSubscriptionPartitionScope'
import {
    isCxaProductApplicable,
    isCxiProductApplicable,
    isCxrProductApplicable,
    isCxsProductApplicable,
    parseBusinessIntegrationOptions
} from '../Common/Utils/CxProductApplicability'
import None from './Roles/Templates/None.json'
// Commons imports.
// Utilities imports.
import { getSession } from '../Common/API/CommonAPI'
import { requestGetJSON, StatusCodes } from '../../NonReact/common/API/REST/RESTAPIClient'
import { IconName } from '../Common/Components/Icon'
import {
    AgentSelectItem,
    DeviceSelectItem,
    HandleChange,
    SelectItem,
    SelectOption,
    SelectOptions
} from '../Common/Components/Selects/Types'
import Tabs from '../Common/Components/Tabs'
import { Tab } from '../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../Common/Constants/HookFormEventNames'
import { CMEResponse, SessionAPIResponse } from '../../NonReact/Types/Types'
import { CapabilityBitFlags, telSysCapabilityBitFlags } from '../CMEAddModify/Constants'
import { LanguageContext, MessageContext, ReactState } from '../Common/Contexts'
import UserInfoContext, { UserInfoContextType } from '../Common/Contexts/UserInfoContext'
import { MultiRequestLoading, useMultiRequestLoading } from '../Common/Hooks/useMultiRequestLoading'
import { LanguageConfig, TelSysIntegrationMode } from '../Common/Types/Types'
import { AddUserLoading } from './AddUserLoading'
import { mapReportingAccessToLevel, mapToFormState, mapToServerState } from './AddUserMapper'
import { AddUserModalID, DEFAULT_CME_ID, LANGUAGE_DEFAULT, SERVER_ALL_VALUE, SERVER_NONE_VALUE } from './Constants'
import ExtensionEndpoint, { FormEndpoint } from './ExtensionEndpoint/ExtensionEndpoint'
import {
    AGENTS_PERM_NAME,
    CALL_RECORDING_NAME,
    CME_PERM_NAME,
    CONCURRENT_SIGNINS_COMPONENT_NAME,
    CXR_SUBSCRIPTION_NAME,
    CXS_SUBSCRIPTION_NAME,
    DEVICES_PERM_NAME,
    EMAIL_COMPONENT_NAME,
    ENDPOINT_AGENT_NAME,
    ENDPOINT_DEVICE_NAME,
    ENHANCED_FEATURES_COMPONENT_NAME,
    FULLNAME_COMPONENT_NAME,
    PARTITION_PERM_NAME,
    READ_ONLY_COMPONENT_NAME,
    REPORT_TEMPLATE_USER_COMPONENT_NAME,
    REPORTING_API_COMPONENT_NAME,
    REPORTING_DEFAULT_REPORTS_COMPONENT_NAME,
    REPORTING_LEVEL_NAME,
    ROLE_COMPONENT_NAME,
    ROLE_PERMS_NAMESPACE,
    TEL_SYS_PERM_NAME,
    USERNAME_COMPONENT_NAME
} from './FieldNames'
import FooterActions from './FooterActions'
import Permissions, { FormPermissions } from './Permissions/Permissions'
import Reporting from './Reporting/Reporting'
import { ReportingAccessLevels } from './Reporting/ReportingAccessLevels'
import { Role } from './Roles/Role'
import { REPORTING_USER_ROLE_ID, RoleTemplate } from './Roles/Templates'
import Settings from './Settings/Settings'
import {
    ApplicationUserFormData,
    Capabilities,
    FilterValues,
    InitialApplicationUserValues,
    PreferredReportingAccessLevel,
    UserRole,
    UserRoleMetadata,
    UserRoleResponse
} from './Types'
import { getLanguageConfig } from '../Common/API/ConfigAPI'
import { StandardOptionGroup } from '../Common/Components/Selects/GroupedSelect/GroupedSelect'
import { isBitmaskMatching } from '../Common/Utils/BitwiseOperations'
import { APIKeys } from './APIKeys/APIKeys'
import { useFeatureFlag } from '../Common/Hooks/useFeatureFlag'
import { CX_SCORE_FEATURE_FLAG, CXI_FEATURE_FLAG_KEY_FOR_TELSYS } from '../Common/Constants/FeatureFlags'
import { DEFAULT_TIMEZONE } from '../TimezoneModal/Constants/Constants'
import {
    AdminUserBody,
    AdminUserResponse,
    ApplicationUserResponse,
    JSONResponse
} from '../../NonReact/common/Types/Types'
import { createUser, getCME, getUser, updateUser } from '../../NonReact/common/API/EntityAPIClient'
import { ApplicationUser } from '../../NonReact/common/Types/AddUser/AddUserTypes'
import { TelSysTypes } from '../../NonReact/constants/TelephonyServerTypes'
import { CxsTelsysMode } from '../../NonReact/constants/CxsTelsysModes'
import { resolveCxsTelsysMode } from '../../NonReact/utils/CxsSubscriptionAccessUtils'
import { BusinessOption, INTEGRATION_TYPE, IntegrationType } from '../TelSysConfiguration/Constants/Constants'
import UserDetails from './UserDetails/UserDetails'
import { CxIntegrationLicence } from '../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { CXScoreLicences } from '../../NonReact/constants/CXScoreLicences'
import { CXRecordLicences } from '../../NonReact/constants/CXRecordLicences'
import { RulesUtils } from '../Common/Rules/Rules'
import { mergeInitialUserFormValues } from './Utils/mergeInitialUserFormValues'
import { getInitialScopeDeviceToHydrate } from './Utils/getInitialScopeDeviceToHydrate'

/**
 * Component properties type.
 */
type Props = {
    viewOnly?: boolean
    closeModal: () => void
    filters?: FilterValues
    initialValues?: InitialApplicationUserValues
    preferredReportingAccessLevels?: PreferredReportingAccessLevel[]
    selectedUser?: string
    refreshAction: () => void
}

type DefaultAvailables = [SelectOptions<number>, SelectOptions<number>]

/**
 * Get the available entities defaults.
 *
 * @param endpoint - Form endpoints from the server.
 *
 * @returns - An array of defaults.
 */
const getAvailableEntitiesDefaults = (endpoint?: FormEndpoint): DefaultAvailables => [
    [{ label: LOADING_STRING, value: endpoint?.telephonyserver ?? 0 }],
    [{ label: LOADING_STRING, value: endpoint?.partition ?? 0 }]
]

/**
 * Configuration tabs container component.
 *
 * @param closeModal Modal close callback.
 * @param selectedUser The user of the selected user row from the list page.
 * @param refresh A function to refresh the list page on submission of the form.
 */
const AddUserTabs: React.FC<Props> = ({
    closeModal,
    selectedUser,
    filters,
    initialValues,
    preferredReportingAccessLevels,
    refreshAction,
    viewOnly
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('AddUserLanguage')
    language.loadBundle('LanguageErrorCodes')

    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const { displayError } = useContext(MessageContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    // eslint-disable-next-line
    const isDataLoadingState: ReactState<boolean> = useState<boolean>(!!selectedUser)
    const [isDataLoading, setIsDataLoading]: ReactState<boolean> = isDataLoadingState
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isValidationPopperOpen, setIsValidationPopperOpen]: ReactState<boolean> = useState<boolean>(false)
    const [telSysType, setTelSysType]: ReactState<Nullable<TelSysTypes>> = useState<Nullable<TelSysTypes>>(
        TelSysTypes.UNKNOWN
    )
    const [callControlOptionsForCxs, setCallControlOptionsForCxs]: ReactState<Nullable<string>> =
        useState<Nullable<string>>(null)

    // Teams CXS gating: undefined until the telephony server's integration mode has loaded, null when none reported.
    const [telSysIntegrationMode, setTelSysIntegrationMode]: ReactState<Nullable<TelSysIntegrationMode>> =
        useState<Nullable<TelSysIntegrationMode>>(undefined)
    const [telSysIntegrationModeOptions, setTelSysIntegrationModeOptions]: ReactState<Nullable<string>> =
        useState<Nullable<string>>(undefined)
    const [telSysPartitionLicences, setTelSysPartitionLicences]: ReactState<Nullable<readonly string[]>> =
        useState<Nullable<readonly string[]>>(undefined)
    const [attachedDevice, setAttachedDevice]: ReactState<Nullable<DeviceSelectItem>> =
        useState<Nullable<DeviceSelectItem>>()

    // Active tab ID.
    const [activeTabID, setActiveTabID] = useState<number>(AddUserModalID.USER_DETAILS)

    const [showSyncPermissionsCheckBox, setShowSyncPermissionsCheckBox]: ReactState<boolean> = useState<boolean>(false)
    const [telSysIntegrationType, setTelSysIntegrationType]: ReactState<Nullable<IntegrationType>> =
        useState<Nullable<IntegrationType>>(undefined)
    // Historic-only flag for the selected MS Teams telsys, lifted from the Reporting tab so the CX
    // Analytics gate can keep the reporting-level dropdown available on Business Historic (APD-13426).
    const [isTelSysHistoricOnly, setIsTelSysHistoricOnly]: ReactState<Nullable<boolean>> =
        useState<Nullable<boolean>>(undefined)

    // eslint-disable-next-line
    const { isLoading, appendLoaderPromise }: MultiRequestLoading = useMultiRequestLoading({
        loaderState: isDataLoadingState
    })

    const { SessionInfo }: AdminUserResponse = userInfo as AdminUserResponse

    const disableAPIKeysTab: Nullable<boolean> = useFeatureFlag('disableAPIKeysTab')

    /**
     * @returns Whether a scalar filter value from the admin list should be applied as an add-user default.
     */
    const isUsableFilterScalar = (value: unknown): boolean => {
        if (IsVarEmpty(value)) return false

        if (typeof value === 'string') return isStringNotEmpty(value)

        if (typeof value === 'number') return Number.isFinite(value)

        return true
    }

    const getValueForDefault = <T extends string | number | number[]>(value: Nullable<T>, defaultValue: T): T => {
        if (Array.isArray(value)) {
            return !selectedUser && value && value.length === 1 ? value : defaultValue
        }
        return !selectedUser && isUsableFilterScalar(value) ? (value as T) : defaultValue
    }

    const defaultValues: MutableRefObject<ApplicationUserFormData> = useRef(
        mergeInitialUserFormValues(
            {
                details: {
                    defaultlanguage: SessionInfo?.Language ?? LANGUAGE_DEFAULT,
                    email: EMPTY_STRING,
                    fullname: EMPTY_STRING,
                    sendwelcomeemail: false,
                    username: EMPTY_STRING,
                    password: EMPTY_STRING,
                    passwordchangerequired: true,
                    cxilicence: CxIntegrationLicence.NONE,
                    timezone: DEFAULT_TIMEZONE
                },
                permissions: {
                    cmenvironment: getValueForDefault(filters?.cmeID, DEFAULT_CME_ID),
                    telephonyserver: getValueForDefault(filters?.telSysID, ALL_VALUE),
                    partition: getValueForDefault(filters?.partitionID ? [filters?.partitionID] : undefined, [
                        ALL_VALUE
                    ]),
                    device: SERVER_ALL_VALUE,
                    agent: SERVER_ALL_VALUE,
                    synchronisesupervisorpermissions: false
                },
                extensionendpoint: {
                    telephonyserver: getValueForDefault(filters?.telSysID, ALL_VALUE),
                    partition: getValueForDefault(filters?.partitionID, ALL_VALUE),
                    device: SERVER_NONE_VALUE,
                    agent: SERVER_NONE_VALUE
                },
                settings: {
                    prompttos: true,
                    notifypasswordchange: true
                },
                reporting: {
                    defaultreports: SERVER_NONE_VALUE
                },
                apiKeys: {
                    name: EMPTY_STRING,
                    expiry: null
                }
            },
            initialValues
        )
    )

    // Form context.
    const methods: UseFormMethods = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const isCXIFeatureEnabled: boolean =
        useFeatureFlag(CXI_FEATURE_FLAG_KEY_FOR_TELSYS[telSysType as TelSysTypes], false) ?? false

    // eslint-disable-next-line
    const {
        reset,
        handleSubmit,
        errors,
        watch,
        setValue,
        trigger,
        formState: { isValid }
    }: UseFormMethods = methods

    const hasAppliedAdminListScopePrefillRef: MutableRefObject<boolean> = useRef<boolean>(false)

    /**
     * Re-applies admin list filter scope to the permissions fields once per add-user mount.
     *
     * Some permission controls (CME select, TelSys combo) do not reliably pick up {@link useForm} defaults on the
     * first paint; pushing the same values through {@link setValue} after mount keeps the UI aligned with list filters.
     */
    useEffect((): void => {
        if (selectedUser || hasAppliedAdminListScopePrefillRef.current || !filters) return

        const hasConcreteCmeFilter: boolean = isStringNotEmpty(filters.cmeID)
        const hasConcreteTelSysFilter: boolean = isVarNotEmpty(filters.telSysID) && Number.isFinite(filters.telSysID)
        const hasConcretePartitionFilter: boolean =
            isVarNotEmpty(filters.partitionID) && Number.isFinite(filters.partitionID)

        if (!hasConcreteCmeFilter && !hasConcreteTelSysFilter && !hasConcretePartitionFilter) return

        if (hasConcreteCmeFilter) {
            setValue(CME_PERM_NAME, filters.cmeID)
        }

        if (hasConcreteTelSysFilter) {
            setValue(TEL_SYS_PERM_NAME, filters.telSysID)
        }

        if (hasConcretePartitionFilter) {
            setValue(PARTITION_PERM_NAME, [filters.partitionID as number])
        }

        hasAppliedAdminListScopePrefillRef.current = true
    }, [filters, selectedUser, setValue])

    const errorHandler = (e: RESTAPIError): void => {
        displayError(e)

        if (isMounted.current) {
            setIsDataLoading(false)
        }
    }

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
    }

    const [reportingLevelOptions, setReportingLevelOptions]: ReactState<Nullable<StandardOptionGroup>[]> = useState<
        Nullable<StandardOptionGroup>[]
    >([])
    const [reportLevelOptionsLoading, setReportLevelOptionsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [permissionsReportingLevel, setPermissionReportingLevel]: ReactState<Nullable<string[]>> =
        useState<Nullable<string[]>>()
    const [adminPermissions, setAdminPermissions]: ReactState<string[]> = useState<string[]>([])
    const [currentPermissions, setCurrentPermissions]: ReactState<string[]> = useState<string[]>([])
    const [scope, setScope]: ReactState<Definable<FormPermissions>> = useState<Definable<FormPermissions>>()
    const [endpoint, setEndpoint]: ReactState<Definable<FormEndpoint>> = useState<Definable<FormEndpoint>>()
    const [callRec, setCallRec]: ReactState<Definable<string>> = useState<Definable<string>>()
    const [defaultUsername, setDefaultUsername]: ReactState<Definable<string>> = useState<string>()
    const [loadedCxsSubscription, setLoadedCxsSubscription]: ReactState<Definable<CXScoreLicences>> =
        useState<Definable<CXScoreLicences>>()
    const [loadedCxrSubscription, setLoadedCxrSubscription]: ReactState<Definable<CXRecordLicences>> =
        useState<Definable<CXRecordLicences>>()
    const [loadedReportingLevel, setLoadedReportingLevel]: ReactState<Definable<ReportingAccessLevels>> =
        useState<Definable<ReportingAccessLevels>>()
    const [hasReset, setHasReset]: ReactState<boolean> = useState<boolean>(false)
    const permissionAvailableDevice: ReactState<SelectItem[]> = useState<SelectItem[]>([])
    const endpointAvailableDevices: ReactState<DeviceSelectItem[]> = useState<DeviceSelectItem[]>([
        { label: '', value: endpoint?.device ?? '' }
    ])
    const endpointAvailableAgents: ReactState<SelectItem[]> = useState<SelectItem[]>([
        { label: '', value: endpoint?.agent ?? '' }
    ])
    const permissionAvailableAgents: ReactState<SelectItem[]> = useState<SelectItem[]>([])
    const [serverError, setServerError]: ReactState<Definable<string>> = useState<string>()
    const isRootScopeState: ReactState<boolean> = useState<boolean>(false)
    const [languageConfig, setLanguageConfig]: ReactState<Nullable<LanguageConfig>> =
        useState<Nullable<LanguageConfig>>()

    const [isCXIOnlyMode, setIsCXIOnlyMode]: ReactState<boolean> = useState<boolean>(false)

    const [customRolesResponse, setCustomRolesResponse]: ReactState<Nullable<UserRoleResponse>> =
        useState<Nullable<UserRoleResponse>>()
    const hasHydratedInitialScopeDevice: MutableRefObject<boolean> = useRef<boolean>(false)

    const [telSysDefault, partitionDefault]: DefaultAvailables = getAvailableEntitiesDefaults(endpoint)

    const [allAvailableTelServers, setAllAvailableTelServers]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(telSysDefault)
    const [availableTelServers, setAvailableTelServers]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(allAvailableTelServers)
    const [endpointAvailablePartitions, setEndpointAvailablePartitions]: ReactState<SelectOptions<number>> =
        useState<SelectOptions<number>>(partitionDefault)

    const customUserRoles: Nullable<RoleTemplate[]> = useMemo(
        (): Nullable<RoleTemplate[]> =>
            customRolesResponse?.Data?.Items?.map(
                ({
                    Name,
                    RoleID,
                    Description,
                    RequiresReporting,
                    Permissions: RolePermissions,
                    IsCustomizedRole
                }: UserRole) => ({
                    name: Name,
                    value: RoleID,
                    description: Description,
                    requiresReporting: RequiresReporting,
                    permissions: RolePermissions,
                    isCustomRole: IsCustomizedRole
                })
            ),
        [customRolesResponse]
    )
    const customRoleMetadata: Nullable<UserRoleMetadata> = useMemo(
        (): Nullable<UserRoleMetadata> => customRolesResponse?.Metadata,
        [customRolesResponse]
    )

    const hasPermissionsChanged: boolean = !deepEqual(adminPermissions, currentPermissions)

    const reportingLevelValue: string = watch(REPORTING_LEVEL_NAME)
    const cmeValue: string = watch(CME_PERM_NAME)

    const isEssentialsUser: boolean = reportingLevelValue === ReportingAccessLevels.ESSENTIALS

    // Business Real-Time User (Professional on MS Teams Business) is device-scoped, so its username is
    // taken from the permission device list, like an Essentials user.
    const isBusinessRealTimeUser: boolean =
        telSysType === TelSysTypes.MS_TEAMS &&
        telSysIntegrationType === INTEGRATION_TYPE.BUSINESS &&
        reportingLevelValue === ReportingAccessLevels.PROFESSIONAL

    const endpointDeviceValue: string = watch(ENDPOINT_DEVICE_NAME)
    const endpointAgentValue: string = watch(ENDPOINT_AGENT_NAME)
    const permissionDeviceValue: string = watch(DEVICES_PERM_NAME)
    const permissionAgentValue: string = watch(AGENTS_PERM_NAME)
    const cxsSubscriptionValue: Nullable<string> = watch(CXS_SUBSCRIPTION_NAME)
    const cxrSubscriptionValue: Nullable<string> = watch(CXR_SUBSCRIPTION_NAME)
    const usernameValue: string = watch(USERNAME_COMPONENT_NAME)
    const emailValue: string = watch(EMAIL_COMPONENT_NAME)
    const telSysValue: Nullable<number> = watch(TEL_SYS_PERM_NAME)
    const permissionPartitionIds: Nullable<number[]> = watch(PARTITION_PERM_NAME)

    const isCXScoreFeatureEnabled: Nullable<boolean> = useFeatureFlag(CX_SCORE_FEATURE_FLAG)

    const currentTelsysReportingLevel: Nullable<TelSysTypes> = useMemo((): Nullable<TelSysTypes> => {
        if (isVarNotEmpty(telSysValue) && isVarNotEmpty(allAvailableTelServers)) {
            const foundTelSys: Definable<SelectOption<number>> = allAvailableTelServers.find(
                (telSys: SelectOption<number>): boolean => telSys.value === telSysValue
            )

            return foundTelSys?.telSysType ?? null
        }
        return null
    }, [telSysValue, allAvailableTelServers])

    const resolvedTelSysTypeForCxs: Nullable<TelSysTypes> = useMemo(
        (): Nullable<TelSysTypes> =>
            telSysType !== TelSysTypes.UNKNOWN ? telSysType : currentTelsysReportingLevel ?? TelSysTypes.UNKNOWN,
        [currentTelsysReportingLevel, telSysType]
    )

    const telSysBusinessOptions: Definable<readonly BusinessOption[]> = useMemo(
        (): Definable<readonly BusinessOption[]> => parseBusinessIntegrationOptions(telSysIntegrationModeOptions),
        [telSysIntegrationModeOptions]
    )

    const isCxsSubscriptionApplicable: boolean = useMemo(
        (): boolean =>
            !!isCXScoreFeatureEnabled &&
            isCxsProductApplicable(
                resolvedTelSysTypeForCxs,
                telSysBusinessOptions,
                telSysPartitionLicences,
                callControlOptionsForCxs
            ) &&
            isCxsSubscriptionScopeAllowedForAddUser(permissionPartitionIds),
        [
            callControlOptionsForCxs,
            isCXScoreFeatureEnabled,
            permissionPartitionIds,
            resolvedTelSysTypeForCxs,
            telSysBusinessOptions,
            telSysPartitionLicences
        ]
    )

    const isCxrSubscriptionApplicable: boolean =
        !!isCXScoreFeatureEnabled && isCxrProductApplicable(telSysBusinessOptions)

    // CXS options are mapped to the reporting tier for BroadWorks (and a Simulator emulating it) - APD-13382 -
    // and MS Teams Business (APD-12730 / US018). Both offer CXS Agent only for Call Centre Agent and CXS Manager
    // only for Call Centre Supervisor, matching the backend; the two modes differ only for None reporting.
    const cxsTelsysMode: Definable<CxsTelsysMode> = resolveCxsTelsysMode(
        resolvedTelSysTypeForCxs,
        callControlOptionsForCxs,
        resolvedTelSysTypeForCxs === TelSysTypes.MS_TEAMS && telSysIntegrationType === INTEGRATION_TYPE.BUSINESS
    )

    const isWebexAgentOrSupervisorUser: boolean =
        (reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_AGENT ||
            reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR) &&
        currentTelsysReportingLevel === TelSysTypes.WEBEX_CALLING
    const isWaitingForCxsSubscriptionApplicability: boolean =
        (!!selectedUser && !hasReset) ||
        isCxsSubscriptionApplicabilityPendingForAddUser({
            selectedUser,
            isCXScoreFeatureEnabled,
            permissionPartitionIds,
            telSysValue,
            telSysType: resolvedTelSysTypeForCxs,
            telSysIntegrationModeOptions
        })

    const triggerEndpointValidation = (): void => {
        trigger(ENDPOINT_DEVICE_NAME)
        trigger(ENDPOINT_AGENT_NAME)
    }

    const triggerEndpointValidationAfterFormUpdate = (): void => {
        setTimeout(triggerEndpointValidation)
    }

    const getCustomRoles = (): Promise<void> =>
        new Promise((resolve, reject): void => {
            appendLoaderPromise(
                requestGetJSON<UserRoleResponse>(userRoleURL())
                    .then((userRoleResponse: JSONResponse<UserRoleResponse>): void => {
                        if (isMounted.current) {
                            setCustomRolesResponse(userRoleResponse)
                            resolve()
                        }
                    })
                    .catch((e: RESTAPIError): void => {
                        errorHandler(e)
                        reject(e)
                    })
            )
        })

    const handleReportingLevelChange: HandleChange = useCallback(
        (newValue: string, oldValue?: string): void => {
            if (newValue !== ReportingAccessLevels.NONE && watch(ROLE_COMPONENT_NAME) === None.value) {
                setValue(ROLE_COMPONENT_NAME, REPORTING_USER_ROLE_ID)
            }

            if (newValue === ReportingAccessLevels.NONE) {
                setValue(REPORTING_DEFAULT_REPORTS_COMPONENT_NAME, undefined)
                setValue(REPORTING_API_COMPONENT_NAME, false)
                setValue(ENHANCED_FEATURES_COMPONENT_NAME, false)
                setValue(CALL_RECORDING_NAME, SERVER_NONE_VALUE)
                setValue(READ_ONLY_COMPONENT_NAME, false)
                setValue(REPORT_TEMPLATE_USER_COMPONENT_NAME, false)
            }

            if (
                oldValue === ReportingAccessLevels.ESSENTIALS &&
                (newValue === ReportingAccessLevels.PROFESSIONAL || newValue === ReportingAccessLevels.ENTERPRISE)
            ) {
                setValue(ENDPOINT_DEVICE_NAME, permissionDeviceValue)
            }

            if (
                newValue === ReportingAccessLevels.ESSENTIALS ||
                newValue === ReportingAccessLevels.PROFESSIONAL ||
                newValue === ReportingAccessLevels.CALL_CENTRE_AGENT
            ) {
                setValue(READ_ONLY_COMPONENT_NAME, true)
                setValue(ENHANCED_FEATURES_COMPONENT_NAME, false)
                setValue(REPORTING_API_COMPONENT_NAME, false)
                setValue(REPORT_TEMPLATE_USER_COMPONENT_NAME, false)
                setValue(CONCURRENT_SIGNINS_COMPONENT_NAME, false)
                setValue(AGENTS_PERM_NAME, SERVER_ALL_VALUE)
            }

            if (newValue === ReportingAccessLevels.PROFESSIONAL) {
                setValue(DEVICES_PERM_NAME, SERVER_ALL_VALUE)
                setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)

                setUserdetailsFromDevice(endpointDeviceValue)
            }

            if (newValue === ReportingAccessLevels.CALL_CENTRE_AGENT) {
                setValue(DEVICES_PERM_NAME, SERVER_ALL_VALUE)
                setValue(ENDPOINT_DEVICE_NAME, SERVER_NONE_VALUE)
                setValue(ENDPOINT_AGENT_NAME, SERVER_NONE_VALUE)

                setUserdetailsFromAgent(endpointAgentValue)
            }

            if (newValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR) {
                setValue(READ_ONLY_COMPONENT_NAME, false)
            }

            trigger(ROLE_COMPONENT_NAME)
            trigger(DEVICES_PERM_NAME)

            // Validate the endpoint device/agent in the same tick as the permission device so both
            // required-selection indicators surface together on the switch, not one after the other.
            // The deferred pass re-checks once dependent endpoint fields settle.
            triggerEndpointValidation()
            triggerEndpointValidationAfterFormUpdate()
        },
        [endpointAgentValue, endpointDeviceValue, permissionDeviceValue]
    )

    useEffect((): void => {
        getCustomRoles()
    }, [])

    useEffect(() => {
        appendLoaderPromise(
            getLanguageConfig()
                .then((langConfig: LanguageConfig): void => {
                    if (isMounted.current) {
                        setLanguageConfig(langConfig)
                    }
                })
                .catch(displayError)
        )

        if (!IsStringEmpty(selectedUser)) {
            appendLoaderPromise(
                getUser(selectedUser)
                    .then((response: JSONResponse<ApplicationUserResponse>): void => {
                        if (isMounted.current) {
                            // Traverse response object.
                            const data: Definable<ApplicationUser> = response?.Data

                            if (data && isMounted?.current) {
                                setLoadedCxsSubscription(
                                    (data.Scope.CXScoreLicenseType as CXScoreLicences) ?? CXScoreLicences.NONE
                                )
                                setLoadedCxrSubscription(
                                    (data.Scope.CXRLicenseType as CXRecordLicences) ?? CXRecordLicences.NONE
                                )
                                setLoadedReportingLevel(
                                    // Map through the same reporting-access mapping the form uses so the
                                    // loaded and current reporting levels are compared in one representation;
                                    // otherwise a Teams Business licence (e.g. "Manager") is mis-detected as a
                                    // reporting-level change and silently resets the CXS/CXR licence (APD-13244).
                                    (mapReportingAccessToLevel(data.Scope.ReportingAccess) as ReportingAccessLevels) ??
                                        ReportingAccessLevels.NONE
                                )
                                reset(
                                    mapToFormState(
                                        data,
                                        setAdminPermissions,
                                        setScope,
                                        setEndpoint,
                                        setCallRec,
                                        setDefaultUsername
                                    )
                                )

                                setTimeout((): void => {
                                    if (isMounted.current) {
                                        reset(
                                            mapToFormState(
                                                data,
                                                setAdminPermissions,
                                                setScope,
                                                setEndpoint,
                                                setCallRec,
                                                setDefaultUsername
                                            )
                                        )

                                        setHasReset(true)
                                    }
                                })

                                permissionAvailableDevice[1]([
                                    {
                                        label: '',
                                        value: data.Scope.DeviceNumber
                                    }
                                ])
                                permissionAvailableAgents[1]([
                                    {
                                        label: '',
                                        value: data.Scope.AgentNumber
                                    }
                                ])
                            }
                        }
                    })
                    .catch(errorHandler)
            )
        }
    }, [])

    const determineValidationErrors = (formErrors: DeepMap<FieldValues, FieldError>): boolean[] => [
        !!formErrors?.details,
        !!formErrors?.permissions,
        !!formErrors?.extensionendpoint,
        !!formErrors?.role,
        !!formErrors?.reporting,
        !!formErrors?.settings
    ]

    const validationErrors: boolean[] = determineValidationErrors(errors)

    const permissions: Record<string, Record<string, boolean>> = watch(ROLE_PERMS_NAMESPACE)

    const isAdministrator: boolean = permissions
        ? Object.values(permissions).some((permission: Record<string, boolean>): boolean =>
              Object.values(permission).some((permissionBool: boolean): boolean => permissionBool)
          )
        : false

    const reportingAPIEnabled: boolean = watch(REPORTING_API_COMPONENT_NAME)

    const submitUserConfiguration = async (state: ApplicationUserFormData): Promise<void> => {
        setIsSubmitting(true)

        const successfulSubmit = (): void => {
            if (isMounted.current) {
                closeModal()
                refreshAction()
            }
        }

        const failedSubmit = (error: RESTAPIError): void => {
            const { errorCode, errorMessage, HTTPStatus }: RESTAPIError = error

            if (HTTPStatus === StatusCodes.OK || HTTPStatus === StatusCodes.OK_NO_CONTENT) {
                displayError(error)
                return successfulSubmit()
            }

            const errorCodeLanguage: Nullable<string> = (LanguageErrorCodes as unknown as Record<string, string>)[
                `Errorcode${errorCode}`
            ]

            if (isMounted.current) {
                setServerError(errorCodeLanguage ? language.get(errorCodeLanguage) : errorMessage)
                setIsSubmitting(false)
            }

            return undefined
        }

        getSession()
            .then((sessionResponse: Nullable<SessionAPIResponse>): void => {
                const serverValue: AdminUserBody = mapToServerState(
                    state,
                    defaultUsername ?? EMPTY_STRING,
                    sessionResponse?.Nonce ?? undefined,
                    telSysType
                )

                selectedUser
                    ? updateUser(selectedUser, serverValue).then(successfulSubmit).catch(failedSubmit)
                    : createUser(serverValue).then(successfulSubmit).catch(failedSubmit)
            })
            .catch(displayError)
    }

    // eslint-disable-next-line
    const invalidFormSubmit = (errors: DeepMap<FieldValues, FieldError>): void => {
        if (!isValid) {
            setActiveTabID(
                determineValidationErrors(errors).findIndex((validationError: boolean): boolean => validationError)
            )
            setIsValidationPopperOpen(true)

            setTimeout((): void => {
                if (isMounted.current) {
                    setIsValidationPopperOpen(false)
                }
            }, 2500)
        }
    }

    const setUserdetailsFromDevice = (deviceNumber?: string): void => {
        if (deviceNumber) {
            const device: Nullable<DeviceSelectItem> = (
                isEssentialsUser || isBusinessRealTimeUser ? permissionAvailableDevice : endpointAvailableDevices
            )[0].find((dev: SelectItem): boolean => dev.value === deviceNumber)

            const newUsernameValue: string =
                telSysType === TelSysTypes.WEBEX_CALLING
                    ? device?.original?.PrimaryEmail ?? EMPTY_STRING
                    : device?.original?.TelSysUsername ?? EMPTY_STRING

            const isWebexSupervisorEmail: boolean =
                telSysType === TelSysTypes.WEBEX_CALLING &&
                reportingLevelValue === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR

            if (isWebexSupervisorEmail && isStringNotEmpty(usernameValue)) return

            setValue(USERNAME_COMPONENT_NAME, newUsernameValue)

            if (IsStringEmpty(emailValue) && RulesUtils.isEmailValid(newUsernameValue)) {
                setValue(EMAIL_COMPONENT_NAME, newUsernameValue)
            }

            setValue(FULLNAME_COMPONENT_NAME, device?.original?.Description ?? EMPTY_STRING)
        }
    }

    const setUserdetailsFromAgent = (agentNumber?: string): void => {
        if (agentNumber) {
            const agent: Nullable<AgentSelectItem> = endpointAvailableAgents[0].find(
                (ag: SelectItem): boolean => ag.value === agentNumber
            )

            const username: string = agent?.original?.TelSysUsername ?? EMPTY_STRING

            setValue(USERNAME_COMPONENT_NAME, username)
            setValue(FULLNAME_COMPONENT_NAME, agent?.original?.Description ?? EMPTY_STRING)

            if (IsStringEmpty(emailValue) && RulesUtils.isEmailValid(username)) {
                setValue(EMAIL_COMPONENT_NAME, username)
            }
        }
    }

    const [capabilities, setCapabilities]: ReactState<Nullable<number>> = useState<Nullable<number>>(undefined)
    const cmeCapabilities: Nullable<Capabilities> = useMemo(
        (): Nullable<Capabilities> =>
            isVarNotEmpty(capabilities)
                ? {
                      legacy: isBitmaskMatching(capabilities, CapabilityBitFlags.CXA_LEGACY),
                      enterprise: isBitmaskMatching(capabilities, CapabilityBitFlags.ENTERPRISE),
                      cxIntegration:
                          IsVarEmpty(telSysType) || telSysType === TelSysTypes.UNKNOWN
                              ? false
                              : isBitmaskMatching(capabilities, telSysCapabilityBitFlags[telSysType])
                  }
                : null,
        [capabilities, telSysType]
    )

    const shouldShowReporting: () => boolean = (): boolean => {
        if (isCXIFeatureEnabled && isCXIOnlyMode) return false
        return reportingLevelValue !== ReportingAccessLevels.NONE
    }

    // The telsys business integration options are the last field set when a telephony server loads,
    // so they resolve from undefined (still loading) to null / a string once fetched. Gate the CX
    // Analytics and CX Integration business-option rules on the loaded value only: while loading,
    // telSysType flips to MS Teams a render before its options arrive, and applying the rules then
    // would transiently hide CX Analytics and reset a user's loaded reporting level to None.
    const areTelSysBusinessOptionsLoaded: boolean = telSysIntegrationModeOptions !== undefined

    // CX Integration is offered only when the telsys carries the CX-Integration business option
    // (Legacy Teams always qualifies). Business options are an MS Teams concept, so other CXI-capable
    // telsys (Webex Calling, BroadWorks) keep their feature-flag + CME-capability gating unchanged.
    const isCXIEnabled: boolean = useMemo(
        (): boolean =>
            !!(isCXIFeatureEnabled && cmeCapabilities?.cxIntegration) &&
            (!areTelSysBusinessOptionsLoaded ||
                telSysType !== TelSysTypes.MS_TEAMS ||
                isCxiProductApplicable(telSysType, telSysIntegrationType, telSysBusinessOptions)),
        [
            cmeCapabilities,
            isCXIFeatureEnabled,
            areTelSysBusinessOptionsLoaded,
            telSysType,
            telSysIntegrationType,
            telSysBusinessOptions
        ]
    )

    // CX Analytics (the reporting-level dropdown) is only offered when it applies to the telsys: on
    // MS Teams it requires the CX-Analytics business option (Legacy and Business Historic aside), and
    // a CXI-Only telsys carries no reporting at all; every other non-Teams telsys keeps offering it.
    const isCxAnalyticsApplicable: boolean = useMemo(
        (): boolean =>
            !areTelSysBusinessOptionsLoaded ||
            isCxaProductApplicable(
                telSysType,
                telSysIntegrationType,
                telSysIntegrationMode,
                telSysBusinessOptions,
                isTelSysHistoricOnly
            ),
        [
            areTelSysBusinessOptionsLoaded,
            telSysType,
            telSysIntegrationType,
            telSysIntegrationMode,
            telSysBusinessOptions,
            isTelSysHistoricOnly
        ]
    )

    useEffect((): void => {
        if (isStringNotEmpty(cmeValue) && (IsStringEmpty(selectedUser) || hasReset)) {
            getCME(cmeValue)
                .then((cme: Nullable<CMEResponse>): void => {
                    if (cme && isVarNotEmpty(cme.Capabilities)) {
                        if (isMounted.current) {
                            setCapabilities(cme.Capabilities)
                        }
                    }
                })
                .catch(displayError)
        }
    }, [cmeValue, selectedUser, hasReset])

    useEffect((): void => {
        const initialScopeDeviceNumber: Nullable<string> = getInitialScopeDeviceToHydrate(
            initialValues?.permissions?.device,
            permissionDeviceValue,
            hasHydratedInitialScopeDevice.current,
            permissionAvailableDevice[0].map((device: SelectItem): string => String(device.value))
        )

        if (!isStringNotEmpty(initialScopeDeviceNumber)) {
            return
        }

        setUserdetailsFromDevice(initialScopeDeviceNumber)
        hasHydratedInitialScopeDevice.current = true
    }, [
        initialValues?.permissions?.device,
        permissionAvailableDevice[0],
        permissionDeviceValue,
        setUserdetailsFromDevice
    ])

    // Keep the extension-endpoint device/agent required indicators in sync with inputs that live on other
    // tabs. A CXS Agent / CXR User licence needs a specific device/agent link, which may be satisfied by a
    // scoped (Permissions tab) device/agent, so re-validate whenever the licence, scope, or reporting level changes.
    useEffect((): void => {
        triggerEndpointValidation()
    }, [cxsSubscriptionValue, cxrSubscriptionValue, permissionDeviceValue, permissionAgentValue, reportingLevelValue])

    // List of all tabs settings.
    const tabs: Nullable<Tab[]> = [
        {
            ID: AddUserModalID.USER_DETAILS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(AddUserLanguage.AddUserLanguage_DetailsTitle),
                iconName: IconName.PERSON,
                onClick: (): void => activateTab(AddUserModalID.USER_DETAILS)
            },
            content: (
                <UserDetails
                    defaultUsername={defaultUsername}
                    isAdmin={isAdministrator}
                    selectedUser={selectedUser}
                    hasPermissionsChanged={hasPermissionsChanged}
                    languageConfig={languageConfig}
                    viewOnly={viewOnly}
                    loadedCxsSubscription={loadedCxsSubscription}
                    loadedCxrSubscription={loadedCxrSubscription}
                    loadedReportingLevel={loadedReportingLevel}
                    allRoles={customUserRoles ?? []}
                    reportingLevelOptions={reportingLevelOptions}
                    isDesktopOnlyMode={isCXIFeatureEnabled && isCXIOnlyMode}
                    telSysType={telSysType}
                    isCXIEnabled={isCXIEnabled}
                    isCxAnalyticsApplicable={isCxAnalyticsApplicable}
                    handleReportingLevelChange={handleReportingLevelChange}
                    isCxsSubscriptionApplicable={isCxsSubscriptionApplicable}
                    businessIntegrationOptions={telSysBusinessOptions}
                    cxsTelsysMode={cxsTelsysMode}
                    reportLevelOptionsLoading={reportLevelOptionsLoading}
                />
            ),
            hasError: validationErrors[AddUserModalID.USER_DETAILS]
        },
        {
            ID: AddUserModalID.PERMISSIONS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(AddUserLanguage.PermissionsTitle),
                iconName: IconName.LOCKED,
                onClick: (): void => activateTab(AddUserModalID.PERMISSIONS)
            },
            content:
                isLoading && IsVarEmpty(userInfo) ? (
                    <></>
                ) : (
                    <Permissions
                        scope={scope}
                        filters={filters}
                        permissionAvailableDevice={permissionAvailableDevice}
                        permissionAvailableAgents={permissionAvailableAgents}
                        viewOnly={viewOnly}
                        setUserdetailsFromDevice={setUserdetailsFromDevice}
                        showSyncPermissionsCheckBox={showSyncPermissionsCheckBox}
                        setAttachedDevice={setAttachedDevice}
                        telsysType={telSysType}
                        integrationType={telSysIntegrationType}
                        isWebexAgentOrSupervisorUser={isWebexAgentOrSupervisorUser}
                    />
                ),
            hasError: validationErrors[AddUserModalID.PERMISSIONS]
        },
        {
            ID: AddUserModalID.EXTENSION_ENDPOINT,
            isDisplayed: !isEssentialsUser,
            tabsMenuItem: {
                title: language.get(AddUserLanguage.ExtensionTitle),
                iconName: IconName.PHONE,
                onClick: (): void => activateTab(AddUserModalID.EXTENSION_ENDPOINT)
            },
            content: (
                <ExtensionEndpoint
                    endpoint={endpoint}
                    permissionAvailableDevice={permissionAvailableDevice}
                    permissionAvailableAgents={permissionAvailableAgents}
                    endpointAvailableDevices={endpointAvailableDevices}
                    endpointAvailableAgents={endpointAvailableAgents}
                    viewOnly={viewOnly}
                    setUserdetailsFromDevice={setUserdetailsFromDevice}
                    setUserdetailsFromAgent={setUserdetailsFromAgent}
                    setAttachedDevice={setAttachedDevice}
                    telsysType={telSysType}
                    allAvailableTelServers={allAvailableTelServers}
                    setAllAvailableTelServers={setAllAvailableTelServers}
                    availablePartitions={endpointAvailablePartitions}
                    setAvailablePartitions={setEndpointAvailablePartitions}
                    availableTelServers={availableTelServers}
                    setAvailableTelServers={setAvailableTelServers}
                    isWebexAgentOrSupervisorUser={isWebexAgentOrSupervisorUser}
                    isCxsSubscriptionApplicable={isCxsSubscriptionApplicable}
                    isCxrSubscriptionApplicable={isCxrSubscriptionApplicable}
                    integrationType={telSysIntegrationType}
                />
            ),
            hasError: validationErrors[AddUserModalID.EXTENSION_ENDPOINT]
        },
        {
            ID: AddUserModalID.ROLE,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(AddUserLanguage.RoleTitle),
                iconName: IconName.ROLE,
                onClick: (): void => activateTab(AddUserModalID.ROLE)
            },
            content: (
                <Role
                    adminPermissions={adminPermissions}
                    setCurrentPermissions={setCurrentPermissions}
                    viewOnly={viewOnly}
                    customUserRoles={customUserRoles ?? []}
                    customRoleMetadata={customRoleMetadata}
                    getCustomRoles={getCustomRoles}
                />
            ),
            hasError: validationErrors[AddUserModalID.ROLE]
        },
        {
            ID: AddUserModalID.REPORTING,
            isDisplayed: shouldShowReporting(),
            tabsMenuItem: {
                title: language.get(AddUserLanguage.ReportingTitle),
                iconName: IconName.REPORT,
                onClick: (): void => activateTab(AddUserModalID.REPORTING)
            },
            content: (
                <Reporting
                    callRec={callRec}
                    isLoading={!!isLoading}
                    viewOnly={viewOnly}
                    isAddMode={!selectedUser}
                    setUserDetailsFromDevice={setUserdetailsFromDevice}
                    setShowSyncPermissionsCheckBox={setShowSyncPermissionsCheckBox}
                    telSysState={[telSysType, setTelSysType]}
                    callControlOptionsState={[callControlOptionsForCxs, setCallControlOptionsForCxs]}
                    setTelSysIntegrationMode={setTelSysIntegrationMode}
                    telSysIntegrationTypeState={[telSysIntegrationType, setTelSysIntegrationType]}
                    isTelSysHistoricOnlyState={[isTelSysHistoricOnly, setIsTelSysHistoricOnly]}
                    attachedDevice={attachedDevice}
                    capabilities={cmeCapabilities}
                    setReportingLevelOptions={setReportingLevelOptions}
                    setPermissionReportingLevel={setPermissionReportingLevel}
                    permissionsReportingLevel={permissionsReportingLevel}
                    preferredReportingAccessLevels={preferredReportingAccessLevels}
                    setIsCXIOnlyMode={setIsCXIOnlyMode}
                    setTelSysIntegrationModeOptions={setTelSysIntegrationModeOptions}
                    setTelSysPartitionLicences={setTelSysPartitionLicences}
                    handleReportingLevelChange={handleReportingLevelChange}
                    setReportLevelOptionsLoading={setReportLevelOptionsLoading}
                />
            ),
            hasError: validationErrors[AddUserModalID.REPORTING]
        },
        {
            ID: AddUserModalID.SETTINGS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(AddUserLanguage.SettingsTitle),
                iconName: IconName.SETTINGS,
                onClick: (): void => activateTab(AddUserModalID.SETTINGS)
            },
            content: (
                <Settings
                    isRootScopeState={isRootScopeState}
                    isAdmin={isAdministrator}
                    scope={scope}
                    viewOnly={viewOnly}
                />
            ),
            hasError: validationErrors[AddUserModalID.SETTINGS]
        },
        ...(disableAPIKeysTab || IsStringEmpty(selectedUser) || (!isAdministrator && !reportingAPIEnabled)
            ? []
            : [
                  {
                      ID: AddUserModalID.API_KEYS,
                      isDisplayed: true,
                      tabsMenuItem: {
                          title: language.get(AddUserLanguage.APIKeys_Title),
                          iconName: IconName.API_KEYS,
                          onClick: (): void => activateTab(AddUserModalID.API_KEYS)
                      },
                      content: <APIKeys selectedUser={selectedUser} viewOnly={viewOnly} />,
                      hasError: validationErrors[AddUserModalID.API_KEYS]
                  }
              ])
    ]

    return (
        <div className='configuration-tabs'>
            {isLoading || isSubmitting || !userInfo || isWaitingForCxsSubscriptionApplicability ? (
                <AddUserLoading />
            ) : (
                <></>
            )}
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitUserConfiguration, invalidFormSubmit)}
                    className={
                        isDataLoading || isSubmitting || isWaitingForCxsSubscriptionApplicability ? HIDE : EMPTY_STRING
                    }
                    noValidate
                >
                    {tabs && <Tabs tabs={tabs} activeTabID={activeTabID} />}
                    {!viewOnly && (
                        <FooterActions
                            serverError={serverError}
                            isValidationPopperOpen={isValidationPopperOpen}
                            closeModal={closeModal}
                        />
                    )}
                </form>
            </FormProvider>
        </div>
    )
}

export default AddUserTabs
